import { useState, type KeyboardEvent } from 'react';
import { Button } from '@nyce/admin-kit/ui';
import type { LogEntry } from '../../api/logs';
import { TableScroller } from '../../components/common';
import { LogEntryDetail, SeverityChip, formatLogTime } from './LogDetail';

export type LogColumn = 'namespace' | 'pod' | 'container';

const keyOf = (e: LogEntry, i: number) => `${e.insertId || i}|${e.timestamp}`;

/**
 * The log table of the Logs page: time, severity chip, optional namespace/pod/container, one-line message. A row opens
 * the whole entry right under it (several can be open, to compare); the lines around stay in view. Shared with the pod
 * logs so both read the same way.
 */
export function LogTable({
  entries,
  onCopied,
  columns = ['namespace', 'pod', 'container'],
}: {
  entries: LogEntry[];
  onCopied: (message: string) => void;
  columns?: LogColumn[];
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const show = (c: LogColumn) => columns.includes(c);
  const span = 3 + columns.length;
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const onKey = (k: KeyboardEvent, key: string) => {
    if (k.key !== 'Enter' && k.key !== ' ') return;
    k.preventDefault();
    toggle(key);
  };
  const anyOpen = entries.some((e, i) => open.has(keyOf(e, i)));
  return (
    <>
      {anyOpen && (
        <div className="oph-log-collapse">
          <Button size="sm" variant="ghost" onClick={() => setOpen(new Set())}>
            Collapse all
          </Button>
        </div>
      )}
      <TableScroller>
        <table className="nyk-ui-table oph-log-table" aria-label="Log entries">
          <thead>
            <tr>
              <th>Time</th>
              <th>Severity</th>
              {show('namespace') && <th>Namespace</th>}
              {show('pod') && <th>Pod</th>}
              {show('container') && <th>Container</th>}
              <th>Message</th>
            </tr>
          </thead>
          {entries.map((e, i) => {
            const key = keyOf(e, i);
            const isOpen = open.has(key);
            return (
              <tbody key={key} className={`nyk-row-group${isOpen ? ' oph-log-is-open' : ''}`}>
                <tr
                  className="nyk-clickable"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => toggle(key)}
                  onKeyDown={(k) => onKey(k, key)}
                >
                  <td className="nyk-nowrap nyk-mono">{formatLogTime(e.timestamp)}</td>
                  <td>
                    <SeverityChip entry={e} />
                  </td>
                  {show('namespace') && <td className="nyk-nowrap">{e.namespace}</td>}
                  {show('pod') && <td className="nyk-nowrap nyk-mono">{e.pod}</td>}
                  {show('container') && <td className="nyk-nowrap">{e.container}</td>}
                  <td className="oph-log-msg" title={e.message}>
                    {e.message}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="oph-log-open">
                    <td colSpan={span}>
                      <LogEntryDetail entry={e} onCopied={onCopied} />
                    </td>
                  </tr>
                )}
              </tbody>
            );
          })}
        </table>
      </TableScroller>
    </>
  );
}
